import { ChangeDetectionStrategy, Component, booleanAttribute, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

/**
 * VendorHub button primitive. Wraps Angular Material's button flavours and
 * projects content via `<ng-content>`. Colours map to the VendorHub tokens
 * rather than Material's default palette so light/dark themes swap cleanly.
 */
export type VhButtonVariant = 'primary' | 'accent' | 'outline' | 'ghost' | 'danger';
export type VhButtonSize = 'sm' | 'md' | 'lg';
export type VhButtonType = 'button' | 'submit' | 'reset';

@Component({
  selector: 'vh-button',
  standalone: true,
  imports: [MatButtonModule, MatProgressSpinnerModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      [type]="type()"
      [disabled]="disabled() || loading()"
      [class]="cssClasses()"
      (click)="handleClick($event)"
    >
      @if (loading()) {
        <mat-progress-spinner mode="indeterminate" diameter="18" />
      }
      <span class="vh-btn__label">
        <ng-content />
      </span>
    </button>
  `,
  styleUrl: './button.component.scss',
})
export class VhButtonComponent {
  readonly variant = input<VhButtonVariant>('primary');
  readonly size = input<VhButtonSize>('md');
  readonly type = input<VhButtonType>('button');
  readonly loading = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly fullWidth = input(false, { transform: booleanAttribute });

  readonly clicked = output<MouseEvent>();

  cssClasses(): string {
    const parts = ['vh-btn', `vh-btn--${this.variant()}`, `vh-btn--${this.size()}`];
    if (this.fullWidth()) parts.push('vh-btn--full');
    if (this.loading()) parts.push('vh-btn--loading');
    return parts.join(' ');
  }

  handleClick(event: MouseEvent): void {
    if (this.disabled() || this.loading()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.clicked.emit(event);
  }
}
